import { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import type { InboxConversationListBodyProps } from '../../CentralInbox.types';

import { routes } from 'config/routes';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { useInfiniteConversationsQuery } from 'shared/legacy/hooks/useInfiniteConversationsQuery';
import { useMarkAsReadBasedOnEsPersonIdMutation } from 'shared/legacy/hooks/useMarkAsReadBasedOnEsPersonIdMutation';
import { useCandidateStore } from 'stores/candidate/candidate.store';
import { useInboxStore } from 'stores/inbox/inbox.store';
import { InboxConversationItem } from '../InboxConversationItem/InboxConversationItem';
import { InboxConversationListSkeleton } from '../InboxConversationListSkeleton/InboxConversationListSkeleton';
import { useStyles } from './InboxConversationListBody.styles';

export const InboxConversationListBody = ({ conversations }: InboxConversationListBodyProps) => {
  const classes = useStyles();
  const setSelectedCandidate = useCandidateStore((state) => state.setSelectedCandidate);
  const teamView = useInboxStore((s) => s.isTeamViewEnabled);
  const conversationScope = useInboxStore((s) => s.selectedFilter);
  const { hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteConversationsQuery({
    teamView,
    conversationScope,
  });
  const pageContext = usePageContext();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const readMutation = useMarkAsReadBasedOnEsPersonIdMutation();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (!target) return;

    const { scrollHeight, scrollTop, clientHeight } = target;
    const distanceFromBottom = scrollHeight - clientHeight - scrollTop;

    if (hasNextPage && distanceFromBottom < 60 && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <div css={classes.root} ref={containerRef} onScroll={handleScroll}>
      {conversations.map((conversation) => (
        <div
          key={conversation.id}
          css={classes.itemPadding}
          onClick={() => {
            if (conversation.candidate) {
              setSelectedCandidate(conversation.candidate?.campaign.id, conversation.candidate);

              if (conversation.unreadCount > 0)
                readMutation.mutateAsync({
                  esPersonId: conversation.esPersonId,
                  status: conversation.candidate.status,
                });
            }

            if (pageContext !== PageContext.CentralInbox) {
              navigate(routes.centralInbox);
            }
          }}>
          <InboxConversationItem conversation={conversation} />
        </div>
      ))}

      {isFetchingNextPage && <InboxConversationListSkeleton />}
    </div>
  );
};
